import React, {
  ChangeEvent,
  FC,
  FormEvent,
  useCallback,
  useState,
} from 'react';
import { TextField } from '@mui/material';
import Modal from 'molecules/Modal';
import {
  ModalActions,
  ModalCloseButton,
  ModalContent,
  ModalSubmitButton,
} from 'molecules/Modal/Modal.styles';
import { cloneDeep } from 'lodash-es';
import { validEmail } from 'utils/helpers/formValidators';
import { useSnackbar } from 'atoms/Snackbar';
import axios from 'axios';

const textFieldStyles = {
  '& .MuiInputBase-root': {
    fontFamily: 'Poppins, sans-serif',
    fontSize: '13px',
    fontWeight: 400,
    lineHeight: '18px',
  },
  '& legend': {
    fontFamily: 'Poppins, sans-serif',
    fontSize: '12px',
    fontWeight: 400,
  },
};

const userFormDefaultValue = {
  firstName: { value: '', error: '' },
  lastName: { value: '', error: '' },
  email: { value: '', error: '' },
  role: { value: '', error: '' },
};

export const UserForm: FC<{
  company_hs_id: string;
  onSubmit: () => void;
  onClose: () => void;
}> = ({ company_hs_id, onSubmit, onClose }) => {
  const { showSnackbar } = useSnackbar();

  const [loading, setLoading] = useState<boolean>(false);
  const [userForm, setUserForm] = useState(userFormDefaultValue);

  const handleUserFormInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: keyof typeof userForm
  ) => {
    const updatedForm = cloneDeep(userForm);
    const newValue = e.target.value;
    if (field === 'email') {
      if (validEmail(newValue)) {
        updatedForm.email.error = '';
      }
    } else if (field === 'firstName') {
      if (newValue) {
        updatedForm.firstName.error = '';
      }
    } else if (field === 'lastName') {
      if (newValue) {
        updatedForm.lastName.error = '';
      }
    }
    updatedForm[field].value = newValue;
    setUserForm(updatedForm);
  };

  const handleCreatingUser = (e: FormEvent) => {
    e.preventDefault();
    const updatedForm = cloneDeep(userForm);
    let error = false;
    if (!validEmail(userForm.email.value)) {
      updatedForm.email.error = 'Please enter valid email address!';
      error = true;
    }
    if (
      !userForm.firstName.value ||
      userForm.firstName.value.trim().length < 2
    ) {
      updatedForm.firstName.error =
        'Please enter your first name, min 2 characters';
      error = true;
    }
    if (!userForm.lastName.value || userForm.lastName.value.trim().length < 2) {
      updatedForm.lastName.error =
        'Please enter your last name, min 2 characters';
      error = true;
    }
    setUserForm(updatedForm);
    if (error) {
      return false;
    }

    handleSubmit();

    return null;
  };

  const handleSubmit = useCallback(async () => {
    setLoading(true);

    try {
      await axios.post(`${process.env.REACT_APP_USER_SERVICE}/create`, {
        name: userForm.firstName.value,
        surname: userForm.lastName.value,
        email: userForm.email.value,
        user_type: 'company_admin',
        company_hs_id: Number(company_hs_id),
      });

      onSubmit();
      setUserForm(userFormDefaultValue);
      showSnackbar('Contact successfully added!', 'success');
    } catch (e) {
      if (e.response.data.error) {
        const message = e.response.data.message || 'Something went wrong!';
        // eslint-disable-next-line no-console
        console.error(e.response.data);
        showSnackbar(message.replaceAll('user', 'contact'), 'error');
      }
    } finally {
      setLoading(false);
    }
  }, [userForm, onClose, company_hs_id]);

  const handleClose = useCallback(() => {
    setUserForm(userFormDefaultValue);
    onClose();
  }, [onClose]);

  return (
    <Modal
      open
      padding24
      maxWidth="md"
      title="Add New Contact"
      onClose={handleClose}
    >
      <>
        <ModalContent>
          <TextField
            fullWidth
            size="small"
            autoComplete="given-name"
            margin="normal"
            label="First name"
            InputLabelProps={{
              shrink: true,
            }}
            value={userForm.firstName.value}
            error={!!userForm.firstName.error}
            helperText={userForm.firstName.error}
            onChange={(event) => handleUserFormInputChange(event, 'firstName')}
            sx={{ ...textFieldStyles }}
          />
          <TextField
            fullWidth
            size="small"
            autoComplete="family-name"
            margin="normal"
            label="Last name"
            InputLabelProps={{
              shrink: true,
            }}
            value={userForm.lastName.value}
            error={!!userForm.lastName.error}
            helperText={userForm.lastName.error}
            onChange={(event) => handleUserFormInputChange(event, 'lastName')}
            sx={{ ...textFieldStyles }}
          />
          <TextField
            fullWidth
            size="small"
            autoComplete="email"
            margin="normal"
            label="Email"
            type="email"
            InputLabelProps={{
              shrink: true,
            }}
            value={userForm.email.value}
            error={!!userForm.email.error}
            helperText={userForm.email.error}
            onChange={(event) => handleUserFormInputChange(event, 'email')}
            sx={{ ...textFieldStyles }}
          />
        </ModalContent>
        <ModalActions $padding24>
          <ModalCloseButton autoFocus variant="outlined" onClick={handleClose}>
            Cancel
          </ModalCloseButton>
          <ModalSubmitButton
            variant="contained"
            onClick={handleCreatingUser}
            loading={loading}
          >
            Add
          </ModalSubmitButton>
        </ModalActions>
      </>
    </Modal>
  );
};
